import { SERVE_ENGLISH_SITE } from "@/lib/locale-paths";
import { ideas } from "@/content/en/ideas";
import { isEnDomainPath } from "@/lib/locale-paths";
import { PERSON_ID, SAME_AS, personSchema } from "@/content/author";
export const SITE_URL = "https://akillisurdurulebilirlik.com";

/**
 * Build an absolute, self-hosted og:image URL.
 * Routes third-party hosts (Pexels, wp.com, actnow4planetearth) through
 * our /api/public/img proxy so social cards don't break when upstream URLs
 * expire and we don't leak referrers to image hosts.
 * Already-absolute URLs on our own domain pass through unchanged.
 */
export function ogImage(url: string | undefined | null, width = 1200): string {
  if (!url) return "";
  try {
    const u = new URL(url);
    const proxied = /(^|\.)wp\.com$/i.test(u.hostname)
      || /(^|\.)pexels\.com$/i.test(u.hostname)
      || /(^|\.)actnow4planetearth\.com$/i.test(u.hostname);
    if (proxied) {
      return `${SITE_URL}/api/public/img/${encodeURIComponent(url)}?w=${width}`;
    }
    return url;
  } catch {
    // Relative path — make absolute against site URL.
    return url.startsWith("/") ? `${SITE_URL}${url}` : url;
  }
}

export const EN_SITE_URL = "https://smartsustainability.ai";

/**
 * Absolute URL for a path: English section paths (/ideas, /concepts, ...)
 * live on the English domain, everything else on the Turkish domain.
 * No trailing slashes are added.
 */
export function absUrl(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  // Root stays "/" (matches the home canonical); deeper paths never keep a
  // trailing slash so canonical and hreflang always agree byte-for-byte.
  const clean = p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
  return `${isEnDomainPath(clean) ? EN_SITE_URL : SITE_URL}${clean}`;
}

/**
 * Build hreflang alternate <link> tags for a given path.
 * Pass the counterpart path when the page truly exists in both locales.
 * If there is no distinct counterpart, only the Turkish hreflang plus
 * x-default (pointing at the same URL) are emitted — never a URL that
 * would 404 or canonicalise elsewhere.
 */
export function hreflangLinks(trPath: string, enPath?: string) {
  // Independent sites: no automatic TR–EN alternates (see SERVE_ENGLISH_SITE).
  if (!SERVE_ENGLISH_SITE) return [] as { rel: string; hreflang: string; href: string }[];
  const tr = absUrl(trPath);
  const en = enPath && enPath !== trPath ? absUrl(enPath) : null;
  return [
    { rel: "alternate", hreflang: "tr", href: tr },
    ...(en ? [{ rel: "alternate", hreflang: "en", href: en }] : []),
    { rel: "alternate", hreflang: "x-default", href: tr },
  ];
}

/**
 * TR article slug -> English counterpart path, derived from the English
 * ideas that declare a `turkishHref`. Used so Turkish article pages emit the
 * reciprocal hreflang="en" link (hreflang must be bidirectional: an EN page
 * pointing at a TR page is ignored unless the TR page points back).
 */
export function enCounterpartForTrPath(trPath: string): string | undefined {
  if (!SERVE_ENGLISH_SITE) return undefined;
  const clean = trPath.replace(/\/+$/, "");
  const match = ideas.find((i) => i.turkishHref?.replace(/\/+$/, "") === clean);
  return match ? `/ideas/${match.slug}` : undefined;
}

/**
 * Both homepages are "/" on their own domain, so hreflangLinks() cannot
 * express them (it dedupes identical paths). Use this on the home routes.
 */
export function homeHreflangLinks() {
  if (!SERVE_ENGLISH_SITE) return [] as { rel: string; hreflang: string; href: string }[];
  return [
    { rel: "alternate", hreflang: "tr", href: `${SITE_URL}/` },
    { rel: "alternate", hreflang: "en", href: `${EN_SITE_URL}/` },
    { rel: "alternate", hreflang: "x-default", href: `${SITE_URL}/` },
  ];
}

/** Stable node ids so Article nodes can reference the author instead of repeating it. */
export const TR_PERSON_ID = PERSON_ID;
export const TR_PUBLISHER_ID = `${SITE_URL}/#publisher`;
export const TR_LOGO_URL = `${SITE_URL}/og-image.jpg`;

export const TR_AUTHOR_SAME_AS = SAME_AS;

/**
 * Person schema for the author — the same single entity as /yazar
 * (src/content/author.ts), so every byline resolves to one Person.
 */
export function trPersonSchema() {
  return { "@context": "https://schema.org", ...personSchema() };
}

/** Publisher (Organization) node for the Turkish publication. */
export function trPublisherSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": TR_PUBLISHER_ID,
    name: "Akıllı Sürdürülebilirlik",
    url: SITE_URL,
    logo: { "@type": "ImageObject", url: TR_LOGO_URL },
    founder: { "@id": TR_PERSON_ID },
  };
}
